import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInboxCount } from '../contexts/InboxCountContext';
import { useInboxList } from '../contexts/InboxListContext';
import { useProductBulkImportContextOptional } from '../contexts/ProductBulkImportContext';
import { isCatalogImportInboxType } from '../components/inboxCatalogImport';
import type { ImportJobStatus } from './useCatalogImportJobInbox';
import { catalogImportAckType } from '../utils/catalogImportInboxAck';

/** Keeps nav badge totals in sync even when the inbox flyout panel is closed. */
export function useInboxBadgeCounts() {
  const { items, isJobSeen } = useInboxList();
  const { syncCounts } = useInboxCount();
  const bulkImport = useProductBulkImportContextOptional();
  const inboxImportItemCount = bulkImport?.inboxImportItemCount ?? 0;
  const importState = bulkImport?.importState ?? {
    importProgress: null,
    result: null,
    err: null,
    jobInFlight: false,
    jobStatus: null,
    activeJobId: null,
  };
  const trackedImportJobId = bulkImport?.trackedImportJobId ?? null;
  const [ackedImportJobs, setAckedImportJobs] = useState<Set<string>>(() => new Set());

  const { jobStatus, activeJobId } = importState;
  const importJobId = trackedImportJobId ?? activeJobId;

  const isImportJobAcked = useCallback(
    (jobId: string, status: ImportJobStatus) => {
      if (!jobId) return false;
      if (ackedImportJobs.has(jobId)) return true;
      return isJobSeen(jobId, catalogImportAckType(status));
    },
    [ackedImportJobs, isJobSeen],
  );

  const visibleItems = useMemo(
    () =>
      items.filter(
        (i) => !(inboxImportItemCount > 0 && isCatalogImportInboxType(i.type)),
      ),
    [items, inboxImportItemCount],
  );

  const importUnseenCount = useMemo(() => {
    let n = 0;
    const jobId = importJobId;
    if (importState.importProgress && importState.jobInFlight && jobId) {
      if (!isImportJobAcked(jobId, jobStatus ?? 'RUNNING')) n += 1;
    }
    if (importState.result && jobId) {
      const st: ImportJobStatus =
        importState.result.failed > 0 && importState.result.created === 0
          ? 'FAILED'
          : 'COMPLETED';
      if (!isImportJobAcked(jobId, st)) n += 1;
    }
    if (
      importState.err &&
      (importState.importProgress || importState.result || importState.jobInFlight) &&
      jobId &&
      !isImportJobAcked(jobId, 'FAILED')
    ) {
      n += 1;
    }
    return n;
  }, [importState, importJobId, jobStatus, isImportJobAcked]);

  useEffect(() => {
    const apiUnseen = visibleItems.filter((i) => !i.seen).length;
    syncCounts({
      total: visibleItems.length + inboxImportItemCount,
      unseen: apiUnseen + importUnseenCount,
    });
  }, [visibleItems, inboxImportItemCount, importUnseenCount, syncCounts]);

  const ackImportJob = useCallback((jobId: string) => {
    setAckedImportJobs((prev) => new Set(prev).add(jobId));
  }, []);

  return { ackImportJob, isImportJobAcked };
}
